import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Headphones, Plus, Lock, Trash2, RefreshCw, BarChart2, Clock, CheckCircle, Calendar } from 'lucide-react';
import { API_BASE, getAdminToken, formatDate, Modal } from './adminUtils';

const formatResponseTime = (minutes) => {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

export const AgentsTab = () => {
  const [supportAgents, setSupportAgents] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [perfPeriod, setPerfPeriod] = useState('week');
  const [loading, setLoading] = useState(false);
  const [perfLoading, setPerfLoading] = useState(false);
  const [newAgentName, setNewAgentName] = useState('');
  const [newAgentEmail, setNewAgentEmail] = useState('');
  const [newAgentPassword, setNewAgentPassword] = useState('');
  const [newAgentDepartment, setNewAgentDepartment] = useState('Support');
  const [creatingAgent, setCreatingAgent] = useState(false);
  const [resetPasswordId, setResetPasswordId] = useState(null);
  const [resetPasswordVal, setResetPasswordVal] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  const fetchSupportAgents = useCallback(async () => {
    const token = getAdminToken(); if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/support-agents`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setSupportAgents((await res.json()).agents);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchPerformance = useCallback(async (period = perfPeriod) => {
    const token = getAdminToken(); if (!token) return;
    setPerfLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/support-agents/performance?period=${period}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setPerformance((await res.json()).performance);
    } catch (e) { console.error(e); }
    finally { setPerfLoading(false); }
  }, [perfPeriod]);

  useEffect(() => { fetchSupportAgents(); }, []);
  useEffect(() => { fetchPerformance(); }, [fetchPerformance]);

  const handleCreateAgent = async () => {
    if (!newAgentName.trim() || !newAgentEmail.trim() || !newAgentPassword.trim()) { toast.error('All fields required'); return; }
    setCreatingAgent(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/support-agents`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${getAdminToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newAgentName, email: newAgentEmail, password: newAgentPassword })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message); setNewAgentName(''); setNewAgentEmail(''); setNewAgentPassword(''); fetchSupportAgents();
    } catch (e) { toast.error(e.message); }
    finally { setCreatingAgent(false); }
  };

  const handleToggleAgent = async (agentId) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/support-agents/${agentId}/toggle`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${getAdminToken()}` }
      });
      if (res.ok) { toast.success('Agent status updated'); fetchSupportAgents(); }
    } catch (e) { toast.error('Failed'); }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm('Delete this agent? Their assigned tickets will be unassigned.')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/support-agents/${agentId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${getAdminToken()}` }
      });
      if (res.ok) { toast.success('Agent deleted, tickets unassigned'); fetchSupportAgents(); }
    } catch (e) { toast.error('Failed'); }
  };

  const handleResetAgentPassword = async () => {
    if (!resetPasswordId || !resetPasswordVal.trim() || resetPasswordVal.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setResettingPassword(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/support-agents/${resetPasswordId}/reset-password`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${getAdminToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: resetPasswordVal })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message); setResetPasswordId(null); setResetPasswordVal('');
    } catch (e) { toast.error(e.message); }
    finally { setResettingPassword(false); }
  };

  return (
    <>
      <div className="space-y-4">
        <Card className="bg-white/5 border-white/10 backdrop-blur">
          <CardHeader><CardTitle className="text-white flex items-center gap-2"><Plus className="w-5 h-5 text-teal-400" />Add Support Agent</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <Input placeholder="Name (Internal)" value={newAgentName} onChange={e => setNewAgentName(e.target.value)} className="bg-white/10 border-white/20 text-white" data-testid="agent-name-input" />
              <Input placeholder="Email" value={newAgentEmail} onChange={e => setNewAgentEmail(e.target.value)} className="bg-white/10 border-white/20 text-white" data-testid="agent-email-input" />
              <Input placeholder="Password" type="password" value={newAgentPassword} onChange={e => setNewAgentPassword(e.target.value)} className="bg-white/10 border-white/20 text-white" data-testid="agent-password-input" />
              <select value={newAgentDepartment} onChange={e => setNewAgentDepartment(e.target.value)} className="bg-white/10 border border-white/20 text-white rounded-md px-3 py-2 text-sm" data-testid="agent-department-select">
                <option value="Support" className="bg-slate-800">General Support</option>
                <option value="Tech Support" className="bg-slate-800">Tech Support</option>
                <option value="Billing" className="bg-slate-800">Billing</option>
                <option value="Enrollment" className="bg-slate-800">Enrollment</option>
                <option value="Lab Support" className="bg-slate-800">Lab Support</option>
              </select>
              <Button onClick={handleCreateAgent} disabled={creatingAgent} className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white" data-testid="create-agent-btn">
                <Plus className="w-4 h-4 mr-2" />{creatingAgent ? 'Creating...' : 'Create'}
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2">Students will not see the agent's personal name — only the department name will be displayed (e.g. "Tech Support Team")</p>
          </CardContent>
        </Card>
        {/* Performance Overview */}
        <Card className="bg-white/5 border-white/10 backdrop-blur">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-white flex items-center gap-2"><BarChart2 className="w-5 h-5 text-teal-400" />Agent Performance</CardTitle>
              <div className="flex items-center gap-2">
                {[{ key: 'week', label: 'This Week' }, { key: 'month', label: 'This Month' }, { key: 'all', label: 'All Time' }].map(({ key, label }) => (
                  <button key={key} onClick={() => setPerfPeriod(key)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium ${perfPeriod === key ? 'bg-teal-500 text-white' : 'bg-white/10 text-slate-400 hover:bg-white/20'}`}>
                    {label}
                  </button>
                ))}
                <Button size="sm" variant="outline" onClick={() => fetchPerformance(perfPeriod)} disabled={perfLoading} className="border-white/20 text-white"><RefreshCw className={`w-3 h-3 ${perfLoading ? 'animate-spin' : ''}`} /></Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {perfLoading ? (
              <div className="flex justify-center py-6"><RefreshCw className="w-6 h-6 text-teal-500 animate-spin" /></div>
            ) : performance.length === 0 ? (
              <p className="text-slate-400 text-center py-6">No performance data yet</p>
            ) : (
              <div className="rounded-xl border border-white/10 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-slate-300">Agent</TableHead>
                      <TableHead className="text-slate-300">Department</TableHead>
                      <TableHead className="text-slate-300 text-center text-amber-300">Open</TableHead>
                      <TableHead className="text-slate-300 text-center text-blue-300">In Progress</TableHead>
                      <TableHead className="text-slate-300 text-center text-emerald-300"><span className="flex items-center justify-center gap-1"><CheckCircle className="w-3 h-3" />Closed</span></TableHead>
                      <TableHead className="text-slate-300 text-center">Total</TableHead>
                      <TableHead className="text-slate-300 text-center"><span className="flex items-center justify-center gap-1"><Clock className="w-3 h-3" />Avg Response</span></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {performance.map(p => (
                      <TableRow key={p.agent_id} className="border-white/10">
                        <TableCell className="text-white font-medium">{p.name}</TableCell>
                        <TableCell><Badge className="bg-teal-500/20 text-teal-300">{p.department}</Badge></TableCell>
                        <TableCell className="text-center text-amber-400 font-medium">{p.open_tickets}</TableCell>
                        <TableCell className="text-center text-blue-400 font-medium">{p.in_progress_tickets}</TableCell>
                        <TableCell className="text-center text-emerald-400 font-medium">{p.closed_tickets}</TableCell>
                        <TableCell className="text-center text-slate-300 font-medium">{p.total_tickets}</TableCell>
                        <TableCell className="text-center">
                          <span className={`font-medium ${p.avg_response_minutes === null ? 'text-slate-500' : p.avg_response_minutes <= 60 ? 'text-emerald-400' : p.avg_response_minutes <= 240 ? 'text-amber-400' : 'text-red-400'}`}>
                            {formatResponseTime(p.avg_response_minutes)}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white/5 border-white/10 backdrop-blur">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2"><Headphones className="w-5 h-5 text-teal-400" />Support Agents</CardTitle>
              <Button size="sm" variant="outline" onClick={() => { fetchSupportAgents(); fetchPerformance(); }} className="border-white/20 text-white"><RefreshCw className="w-3 h-3" /></Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 text-teal-500 animate-spin" /></div> : supportAgents.length === 0 ? (
              <p className="text-slate-400 text-center py-8">No support agents yet</p>
            ) : (
              <div className="rounded-xl border border-white/10 overflow-hidden">
                <Table>
                  <TableHeader><TableRow className="border-white/10"><TableHead className="text-slate-300">Name</TableHead><TableHead className="text-slate-300">Department</TableHead><TableHead className="text-slate-300">Email</TableHead><TableHead className="text-slate-300">Status</TableHead><TableHead className="text-slate-300">Active Tickets</TableHead><TableHead className="text-slate-300">Total Handled</TableHead><TableHead className="text-slate-300">Last Login</TableHead><TableHead className="text-slate-300 text-center">Actions</TableHead></TableRow></TableHeader>
                  <TableBody>{supportAgents.map(a => (
                    <TableRow key={a.id} className="border-white/10">
                      <TableCell className="text-white font-medium">{a.name}</TableCell>
                      <TableCell><Badge className="bg-teal-500/20 text-teal-300">{a.department || 'Support'}</Badge></TableCell>
                      <TableCell className="text-slate-300">{a.email}</TableCell>
                      <TableCell><Badge className={a.is_active ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}>{a.is_active ? 'Active' : 'Disabled'}</Badge></TableCell>
                      <TableCell className="text-teal-400 font-medium">{a.active_tickets || 0}</TableCell>
                      <TableCell className="text-slate-400">{a.total_handled || 0}</TableCell>
                      <TableCell className="text-slate-400 text-sm">{a.last_login ? formatDate(a.last_login) : 'Never'}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Switch checked={a.is_active} onCheckedChange={() => handleToggleAgent(a.id)} />
                          <Button size="sm" variant="outline" onClick={() => { setResetPasswordId(a.id); setResetPasswordVal(''); }} className="border-white/20 text-slate-300" title="Reset Password"><Lock className="w-3 h-3" /></Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDeleteAgent(a.id)} className="bg-red-500/20 hover:bg-red-500/30 text-red-300"><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Password Reset Modal */}
      <Modal show={!!resetPasswordId} onClose={() => { setResetPasswordId(null); setResetPasswordVal(''); }} title="Reset Agent Password">
        <div className="space-y-4">
          <p className="text-slate-400 text-sm">Enter new password for this agent (min 6 characters)</p>
          <Input type="password" placeholder="New password" value={resetPasswordVal} onChange={e => setResetPasswordVal(e.target.value)} className="bg-white/10 border-white/20 text-white" data-testid="reset-password-input" />
          <div className="flex gap-3">
            <Button onClick={handleResetAgentPassword} disabled={resettingPassword || resetPasswordVal.length < 6} className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white" data-testid="reset-password-btn">
              <Lock className="w-4 h-4 mr-2" />{resettingPassword ? 'Resetting...' : 'Reset Password'}
            </Button>
            <Button variant="outline" onClick={() => { setResetPasswordId(null); setResetPasswordVal(''); }} className="border-white/20 text-slate-300">Cancel</Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
